import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";
import Header from "@/components/Header";
import { LayoutDashboard, ShieldCheck, BellRing, ScrollText, Receipt, Globe2, FileText, BarChart3, Settings2, Sparkles, ImageIcon, HelpCircle } from "lucide-react";import { useLang } from "@/lib/i18n";

const NAV = [
  { to: "/app", end: true, key: "dash.title", Icon: LayoutDashboard, testid: "sidenav-registry" },
  { to: "/app/scan", key: "nav.fullscan", Icon: FileText, testid: "sidenav-scan" },
  { to: "/app/image-scan", key: "nav.imagescan", Icon: ImageIcon, testid: "sidenav-image-scan" },
  { to: "/app/document-scan", key: "nav.docscan", Icon: FileText, testid: "sidenav-document-scan" },
  { to: "/app/sites", key: "nav.sites", Icon: Globe2, testid: "sidenav-sites" },
  { to: "/app/proofs", key: "nav.proofs", Icon: ShieldCheck, testid: "sidenav-proofs" },
  { to: "/app/alerts", key: "nav.alerts", Icon: BellRing, testid: "sidenav-alerts" },
  { to: "/app/journal", key: "nav.journal", Icon: ScrollText, testid: "sidenav-journal" },
  { to: "/app/reports", key: "nav.reports", Icon: BarChart3, testid: "sidenav-reports" },
  { to: "/app/subscriptions", key: "nav.subscriptions", Icon: Sparkles, testid: "sidenav-subscriptions" },
  { to: "/app/how-it-works", key: "nav.how", Icon: HelpCircle, testid: "sidenav-how" },
  { to: "/app/billing", key: "backoffice.title", Icon: Receipt, testid: "sidenav-billing" },
];
const ADMIN_NAV = null;  // Admin isolé dans /admin/* (auth séparée)

export default function AppShell() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const { t } = useLang();

  useEffect(() => { if (ready && !user) nav("/login"); }, [ready, user, nav]);
  if (!ready || !user) return <div className="p-10 font-mono text-sm">…</div>;

  const pct = Math.min(100, Math.round(((user.quota_used || 0) / (user.quota_max || 100)) * 100));

  return (
    <div className="min-h-screen bg-background">
      <Header variant="app" />

      {/* Mobile app-nav — pills scroll horizontal */}
      <nav className="md:hidden sticky top-[64px] z-30 bg-white/90 backdrop-blur border-b border-border">
        <div className="max-w-[1400px] mx-auto px-3 py-2 flex gap-1.5 overflow-x-auto scrollbar-none" data-testid="mobile-app-nav">
          {NAV.map(({ to, end, key, Icon, testid }) => (
            <NavLink key={to} to={to} end={end} data-testid={`mobile-${testid}`}
              className={({ isActive }) =>
                `shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors ${isActive ? "bg-primary text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`
              }
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
              <span>{t(key)}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-8 grid md:grid-cols-[240px_1fr] gap-6 md:gap-10">
        <aside className="hidden md:block">
          <div className="sticky top-24">
            <p className="eyebrow mb-4 px-3">{t("nav.workspace")}</p>
            <nav className="flex flex-col gap-1">
              {NAV.map(({ to, end, key, Icon, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors duration-200 ${isActive ? "bg-accent text-primary font-semibold border-l-[3px] border-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground border-l-[3px] border-transparent"}`
                  }
                >
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                  <span>{t(key)}</span>
                </NavLink>
              ))}
            </nav>
            <div className="mt-8 p-5 hairline">
              <div className="flex items-center justify-between mb-1">
                <p className="eyebrow">{t("backoffice.plan")}</p>
                <img src="/brand/logo-icon.png" alt="" className="h-5 w-auto opacity-70" />
              </div>
              <p className="font-serif text-xl capitalize text-primary">{user.plan}</p>
              <div className="mt-3 h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-secondary rounded-full transition-[width] duration-500" style={{ width: `${pct}%` }} />
              </div>
              <p className="text-xs text-muted-foreground mt-2 font-mono">{user.quota_used}/{user.quota_max} · {t("dash.metrics.quota")}</p>
            </div>
          </div>
        </aside>
        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
